import { DownloadOutlined, FileImageOutlined } from '@ant-design/icons';
import { Button, Spin } from 'antd';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { ModelClientFile } from '@app/api/ClientFiles';
import { StreamVideoPlayer } from '@app/components/Chat/components';
import {
  imagesExtensions,
  videoExtensions,
} from '@app/components/Chat/constants';
import type { PhotoSwipeItem } from '@app/components/ImageGallery/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import type { NFiles } from '@app/types';

import type { FC } from 'react';

// @ts-ignore: CSS import without type declarations
import 'photoswipe/style.css';
import '@app/components/ImageGallery/ImageGallery.desktop.scss';

const LoaderOverlay: FC<{ isVisible: boolean }> = ({ isVisible }) => (
  <div className={`loader-overlay ${isVisible ? 'visible' : 'hidden'}`}>
    <Spin size="large" spinning />
  </div>
);

type FileKind = 'image' | 'video' | 'other';

const normalizeExt = (ext: string | null | undefined) =>
  (ext ?? '').trim().toLowerCase();

const getFileKind = (file: Partial<NFiles.ModelClientFile>): FileKind => {
  const ext = normalizeExt(file.Extension);
  const hasStreamId = Boolean(file.ExternalAccessId);

  if (ext === 'webp') {
    return hasStreamId ? 'video' : 'image';
  }

  if (videoExtensions.includes(ext)) return 'video';
  if (imagesExtensions.includes(ext)) return 'image';

  return 'other';
};

const getImageMimeByExt = (ext: string) => {
  switch (ext) {
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'bmp':
      return 'image/bmp';
    case 'webp':
      return 'image/webp';
    case 'jpg':
    case 'jpeg':
    default:
      return 'image/jpeg';
  }
};

interface ImageGalleryProps {
  files: Partial<NFiles.ModelClientFile>[];
  getClientFile: (fileId: number) => Promise<ModelClientFile | null>;
  downloadFile: (id: number, extension: string | null) => Promise<void>;
}

export const ImageGallery: FC<ImageGalleryProps> = ({
  files,
  getClientFile,
  downloadFile,
}) => {
  const { showNotification } = useNotification();
  const galleryRef = useRef<HTMLDivElement>(null);
  const lightBoxRef = useRef<PhotoSwipeLightbox | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    lightBoxRef.current = new PhotoSwipeLightbox({
      pswpModule: () => import('photoswipe'),
      errorMsg:
        'К сожалению, это изображение, пока что, не загрузилось. Попробуйте закрыть это окно и попробовать позднее.',
    });

    lightBoxRef.current.init();

    return () => {
      lightBoxRef.current?.destroy();
      lightBoxRef.current = null;
    };
  }, []);

  const handleThumbnailClick = async (
    event: React.MouseEvent<HTMLAnchorElement>,
    clickedFile: Partial<NFiles.ModelClientFile>
  ) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      const results = await Promise.allSettled(
        imageFiles.map(async (file) => {
          if (!file.Id) throw new Error('File ID missing');

          const imageData = await getClientFile(file.Id);
          if (!imageData?.Data) throw new Error('Failed to load image data');

          const ext = normalizeExt(file.Extension);
          const mime = getImageMimeByExt(ext);

          const src = `data:${mime};base64,${imageData.Data}`;

          return new Promise<PhotoSwipeItem>((resolve, reject) => {
            const img = new Image();
            img.src = src;

            img.onload = () => {
              resolve({
                src,
                width: img.naturalWidth,
                height: img.naturalHeight,
                id: file.Id,
                title: file.Description || file.FileName,
              });
            };

            img.onerror = () => reject(new Error('Failed to load image'));
          });
        })
      );

      const loadedItems = results
        .filter(
          (r): r is PromiseFulfilledResult<PhotoSwipeItem> =>
            r.status === 'fulfilled'
        )
        .map((r) => r.value);

      if (loadedItems.length === 0)
        throw new Error('No images loaded successfully.');

      const clickedItemIndex = loadedItems.findIndex(
        (item) => item.id === clickedFile.Id
      );

      lightBoxRef.current?.loadAndOpen(
        clickedItemIndex !== -1 ? clickedItemIndex : 0,
        loadedItems
      );
    } catch (error) {
      showNotification(error);
    } finally {
      setIsLoading(false);
    }
  };

  const imageFiles = useMemo(
    () => files.filter((f) => getFileKind(f) === 'image'),
    [files]
  );

  return (
    <div className="image-gallery-container" ref={galleryRef}>
      {files.map((file) => {
        const kind = getFileKind(file);

        if (kind === 'video') {
          return (
            <div className="video-container" key={file.Id}>
              <StreamVideoPlayer
                fileId={file?.ExternalAccessId}
                clientId={file?.ClientId}
              />
              <Button
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  if (!file.Id) return;
                  downloadFile(file.Id, file.Extension ?? null);
                }}
                className="image-gallery-load-button"
                icon={<DownloadOutlined />}
              />
              <div className="image-gallery-description">
                {file.Description}
              </div>
            </div>
          );
        }

        // image или other
        return (
          <div key={file.Id} className="image-gallery-item">
            <a
              href=""
              onClick={(e) => handleThumbnailClick(e, file)}
              className="image-gallery-anchor"
            >
              {Boolean(file.Thumbnail) ? (
                <img
                  src={`data:image/jpeg;base64,${file.Thumbnail}`}
                  alt={file?.Description ?? file?.FileName ?? ''}
                  className="image-gallery-preview"
                />
              ) : (
                <div className="image-gallery-thumbnail">
                  <FileImageOutlined className="image-gallery-thumbnail-icon" />
                </div>
              )}

              {kind === 'image' && (
                <Button
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    if (!file.Id) return;
                    downloadFile(file.Id, file.Extension ?? null);
                  }}
                  className="image-gallery-load-button"
                  icon={<DownloadOutlined />}
                />
              )}
            </a>

            <div className="image-gallery-description">{file.Description}</div>
          </div>
        );
      })}
      <LoaderOverlay isVisible={isLoading} />
    </div>
  );
};
